/**
 * Modern Select component with enterprise styling
 */

import React from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options?: SelectOption[];
  label?: string;
  error?: string;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function Select({
  options = [],
  label,
  error,
  helperText,
  className,
  onChange,
  ...props
}: SelectProps) {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-semibold text-gray-900 dark:text-theme-text mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          onChange={onChange}
          className={clsx(
            'w-full pl-4 pr-10 py-2.5 text-sm font-medium',
            'bg-white dark:bg-theme-paper border border-theme-border rounded-lg',
            'text-gray-900 dark:text-theme-text',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'hover:border-theme-border transition-all',
            'appearance-none cursor-pointer',
            error && 'border-error-main focus:ring-error-main',
            className
          )}
          {...props}
        >
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
          {props.children}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-theme-text-secondary pointer-events-none" />
      </div>
      {error && <p className="mt-1.5 text-sm text-error-dark font-medium">{error}</p>}
      {helperText && !error && <p className="mt-1.5 text-sm text-gray-600 dark:text-theme-text-secondary">{helperText}</p>}
    </div>
  );
}
